"use client";

import type { User } from "@supabase/supabase-js";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import type {
  AuthContextValue,
  AuthEventListener,
  AuthStatus,
} from "@/lib/hooks/useAuth";
import { AuthContext } from "@/lib/hooks/useAuth";
import { logError } from "@/lib/logger";
import { createClient } from "@/lib/supabase/client";

interface AuthState {
  status: AuthStatus;
  user: User | null;
}

const INITIAL_AUTH: AuthState = { status: "loading", user: null };

/**
 * App-wide client auth state. Subscribes to Supabase `onAuthStateChange`
 * exactly once; everything else reads `useAuth()` / `useAuthEvent()`.
 */
export function AuthProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const [state, setState] = useState<AuthState>(INITIAL_AUTH);
  const listenersRef = useRef<Set<AuthEventListener>>(new Set());
  // Read from the auth callback, so the subscription never depends on it.
  const routerRef = useRef(router);

  useEffect(() => {
    routerRef.current = router;
  }, [router]);

  // The one and only auth subscription. Never call getSession()/getUser()
  // during render: everything flows from these events.
  useEffect(() => {
    const supabase = createClient();
    let knownUserId: string | null | undefined;

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      const user = session?.user ?? null;
      const userId = user?.id ?? null;
      const identityChanged =
        knownUserId !== undefined && knownUserId !== userId;

      knownUserId = userId;

      // Token refreshes (hourly) and repeated SIGNED_IN events for the same
      // user keep the previous state, so the app doesn't re-render for them.
      setState((previous) =>
        previous.status !== "loading" &&
        previous.user?.id === userId &&
        event !== "USER_UPDATED"
          ? previous
          : { status: user ? "authenticated" : "anonymous", user },
      );

      for (const listener of [...listenersRef.current]) {
        listener(event, user);
      }

      // Server components may render per-user content: re-render them when
      // the signed-in user actually changes (never on INITIAL_SESSION or
      // TOKEN_REFRESHED).
      if (identityChanged) routerRef.current.refresh();
    });

    return () => subscription.unsubscribe();
  }, []);

  const subscribe = useCallback((listener: AuthEventListener) => {
    const listeners = listenersRef.current;

    listeners.add(listener);

    return () => {
      listeners.delete(listener);
    };
  }, []);

  const signOut = useCallback(async () => {
    try {
      const { error } = await createClient().auth.signOut();

      if (error) throw error;

      return { ok: true };
    } catch (error) {
      logError("Sign out failed", error, { component: "AuthProvider" });

      return { ok: false };
    }
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({ ...state, signOut, subscribe }),
    [state, signOut, subscribe],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
